import { ImageBase } from "@/api/images-types";
import { RoleName } from "@/api/users-types";
import { RejectWholeEpisodeDialog } from "@/components/episodes/RejectWholeEpisodeDialog";
import { ManualImageUploadDialog } from "@/components/images/ManualImageUploadDialog";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { EpisodeBreadcrumbs } from "@/components/ui/breadcrumbs/EpisodeBreadcrumbs";
import { Link } from "@/components/ui/Link";
import { BoxLoader } from "@/components/ui/loaders";
import { ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { ImageStateChip } from "@/components/ui/state-chips/ImageStateChip";
import { TagsRow } from "@/components/ui/tags/TagsRow";
import { hasRole } from "@/hooks/use-has-role";
import { useRejectWholeEpisode } from "@/hooks/use-reject-whole-episode";
import { handleDownloadImage, handleDownloadMask } from "@/pages/images/utils";
import { useUser } from "@/queries/auth";
import { useEpisodeById } from "@/queries/episodes/use-episode-by-id";
import { useEpisodeImagesByEpisodeId } from "@/queries/episodes/use-episode-images-by-episode-id";
import { useEpisodeMasksByEpisodeId } from "@/queries/episodes/use-episode-masks-by-episode-id";
import { useEpisodeTextsByEpisodeId } from "@/queries/episodes/use-episode-texts-by-episode-id";
import { useUploadManualImage } from "@/queries/images/use-upload-manual-image";
import {
  imageStateOptions,
  isOnlyOperator,
  rejectionNotesColumn,
  rejectionReasonsColumn,
  submissionStateColumn,
} from "@/utils/datagrid-utils";
import { Image } from "@mui/icons-material";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

export function EpisodeImageList() {
  const { id } = useParams();

  const { data, isLoading, error } = useEpisodeImagesByEpisodeId(id);
  const { data: episode } = useEpisodeById(id);
  const { data: episodeTexts } = useEpisodeTextsByEpisodeId(id);
  const { data: masks } = useEpisodeMasksByEpisodeId(id);
  const { data: user } = useUser();
  const [uploadDialogState, setUploadDialogState] = useState<{
    imageId: string;
    isChildPresented: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    mutate: uploadManualImage,
    isPending: isUploading,
    error: uploadError,
  } = useUploadManualImage();

  const {
    confirmRejectWholeEpisode,
    setConfirmRejectWholeEpisode,
    handleRejectWholeEpisode,
    isRejectingWholeEpisode,
    canRejectEpisode,
    error: rejectWholeEpisodeError,
  } = useRejectWholeEpisode({
    episodeId: id || "",
    shouldNavigateToNext: false,
  });

  const baseImageColumns = useMemo<GridColDef<ImageBase>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 120,
        filterable: false,
        renderCell: (params) => (
          <Link to={`/images/${params.row.id}`}>{params.row.id}</Link>
        ),
      },
      {
        field: "url",
        headerName: "Image",
        width: 340,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const pageText =
            episodeTexts?.pages.find(
              (page) => page.pageNumber === params.row.page,
            )?.text || "No text available";

          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Tooltip
                arrow
                title={
                  <Typography variant="body2" sx={{ maxWidth: 300 }}>
                    {pageText}
                  </Typography>
                }
                placement="top"
              >
                {params.row.url ? (
                  <img
                    src={params.row.url}
                    alt={`Image ${params.row.id}`}
                    style={{ maxWidth: "300px", margin: "auto" }}
                  />
                ) : (
                  <Image sx={{ fontSize: "60px", color: grey[400] }} />
                )}
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "page",
        headerName: "Page",
        width: 120,
        filterable: false,
      },
      {
        field: "isChildPresented",
        headerName: "User Character Present",
        width: 180,
        type: "boolean",
      },
      {
        field: "state",
        headerName: "State",
        align: "center",
        headerAlign: "center",
        width: 180,
        filterOperators: isOnlyOperator,
        renderCell: (params) => {
          return <ImageStateChip state={params.row.state} />;
        },
        type: "singleSelect",
        valueOptions: imageStateOptions,
      },
      submissionStateColumn<ImageBase>(),
      rejectionReasonsColumn<ImageBase>({
        filterable: false,
      }),
      rejectionNotesColumn<ImageBase>({
        filterable: false,
      }),
    ],
    [episodeTexts],
  );

  const manualEditImageColumn: GridColDef<ImageBase> = useMemo(
    () => ({
      field: "needsManualEdit",
      headerName: "Manual Edit",
      width: 100,
      type: "boolean",
      renderCell: (params) => {
        return params.row.needsManualEdit ? "✏️" : "";
      },
    }),
    [],
  );

  const handleUpload = (
    imageId: string,
    episodeId: string,
    imageFile: File,
    maskFile?: File,
  ) => {
    uploadManualImage(
      { imageId, episodeId, imageFile, maskFile },
      {
        onSuccess: () => {
          setUploadDialogState(null);
        },
      },
    );
  };

  const actionsColumn: GridColDef<ImageBase> = useMemo(
    () => ({
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params.row.needsManualEdit) {
          return null;
        }

        const imageMask = masks?.masks.find(
          (m) => m.image.id === params.row.id,
        );

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                handleDownloadImage(
                  id,
                  () => setLoading(true),
                  () => setLoading(false),
                )
              }
            >
              Download Image
            </Button>
            {imageMask && imageMask.url && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDownloadMask(imageMask.url!, imageMask.id)}
              >
                Download Mask
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                setUploadDialogState({
                  imageId: params.row.id,
                  isChildPresented: params.row.isChildPresented,
                })
              }
            >
              Upload
            </Button>
          </Box>
        );
      },
    }),
    [masks, loading],
  );

  const imageColumns = useMemo(() => {
    if (!user) return baseImageColumns;

    const isImageEditor = hasRole(user, RoleName.imageEditor);
    const isVisualsValidator = hasRole(user, RoleName.visualsValidator);

    const canSeeManualEditColumn = isImageEditor || isVisualsValidator;
    const canUploadImages = isImageEditor;

    if (canSeeManualEditColumn) {
      const columns = [...baseImageColumns, manualEditImageColumn];

      if (canUploadImages) {
        columns.push(actionsColumn);
      }

      return columns;
    }

    return baseImageColumns;
  }, [baseImageColumns, manualEditImageColumn, actionsColumn, user]);

  if (!id) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: 2 }}>
      <ApiErrorAlert error={error} />
      <ApiErrorSnackbar error={rejectWholeEpisodeError} />
      <Stack gap={2} mb={2}>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "33px", // height of Button, set in case no buttons are displayed to have consistent height across different cases
          }}
        >
          {episode && (
            <EpisodeBreadcrumbs
              id={episode.id}
              category={episode.category}
              gender={episode.gender}
              story={episode.story}
              subcategory={episode.subcategory}
            />
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {canRejectEpisode && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setConfirmRejectWholeEpisode(true)}
              >
                {"Reject whole story"}
              </Button>
            )}
          </Box>
        </Stack>
        <TagsRow episodeId={id} />
      </Stack>
      <BoxLoader isLoading={isLoading} height={100} />
      <DataGrid
        rows={data?.images}
        loading={loading}
        columns={imageColumns}
        columnHeaderHeight={46}
        rowHeight={200}
        rowSelection={false}
        hideFooter={true}
      />
      <RejectWholeEpisodeDialog
        open={confirmRejectWholeEpisode}
        onClose={() => setConfirmRejectWholeEpisode(false)}
        onConfirm={handleRejectWholeEpisode}
        isLoading={isRejectingWholeEpisode}
      />
      {uploadDialogState && id && (
        <ManualImageUploadDialog
          open={!!uploadDialogState}
          onClose={() => setUploadDialogState(null)}
          imageId={uploadDialogState.imageId}
          episodeId={id}
          isChildPresented={uploadDialogState.isChildPresented}
          onUpload={handleUpload}
          isUploading={isUploading}
        />
      )}
      <ApiErrorSnackbar error={uploadError} />
    </Box>
  );
}
